

import React, { FC, useEffect, useState } from "react";
import { useProperties } from "../../hook/useProperties";
import { FaSpinner } from "react-icons/fa";

interface MonthData {
  [month: string]: {
    status: string;
    openingBalance: string;
  };
}

interface PaymentData {
  balanceAmount: string;
  status: string;
}

interface ApiResponse {
  totalBalanceAmount: string;
  paymentsByMonth: {
    [month: string]: PaymentData;
  };
  propertyOccupied: boolean;
  tenantName: string | null;
  propertyStatus?: string;
}

interface PopupTableProps {
  id: any;
  isOpen: boolean;
  onClose: () => void;
}

const PopupHistory: FC<PopupTableProps> = ({ id, isOpen, onClose }) => {
  const { PropertyHistory } = useProperties();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const data = PropertyHistory(id, selectedYear);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-11
 // const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

 const years = Array.from({ length: 11 }, (_, index) => currentYear - 5 + index);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

  // Function to check if a month is in the future
  const isFutureMonth = (monthName: string): boolean => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June", 
      "July", "August", "September", "October", "November", "December"
    ];
    const monthIndex = monthNames.indexOf(monthName);
    
    return (selectedYear > currentYear) || 
           (selectedYear === currentYear && monthIndex > currentMonth);
  };

  // Function to determine if a month should display as "Nil"
  const shouldDisplayAsNil = (monthName: string, status: string): boolean => {
    // If it's already unoccupied, display as "Nil"
    if (status === "Unoccupied") {
      return true;
    }
    
    // If property is currently vacant and it's a future month, display as "Nil"
    const isPropertyVacant = historyData?.propertyOccupied !== true;
    if (isPropertyVacant && isFutureMonth(monthName)) {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    // console.log("Selected Year:", selectedYear);
  }, [selectedYear]);

  if (data.isLoading) {
    return (
      <div
        className={`fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50 ${
          isOpen ? "block" : "hidden"
        }`}
      >
        <div className="p-10 rounded-md bg-white">
          <p className="flex gap-1 justify-center border p-2 border-primary rounded-lg bg-primary/20 text-black items-center">
            <FaSpinner className="animate-spin mr-2" /> Loading...
          </p>
        </div>
      </div>
    );
  }

  if (data == undefined) {
    return <p>no data</p>;
  }

  const historyData = data?.data?.data as unknown as ApiResponse;

  return (
    <div
      className={`fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50 ${
        isOpen ? "block" : "hidden"
      }`}
      onClick={onClose} // Close when clicking outside
    >
      <div
        className="bg-white rounded-lg shadow-lg overflow-hidden mx-4 w-full lg:w-3/4 lg:max-w-md"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-primary/10 text-black gap-2">
          <div className="flex flex-col items-start">
            <h2 className="text-lg font-bold">Payment Status</h2>
            {historyData?.propertyOccupied === true ? (
              <p className="text-sm text-green-500">
                Occupied: {historyData?.tenantName || "N/A"}
              </p>
            ) : (
              <p className="text-sm text-red-500">Unoccupied</p>
            )}
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-grow sm:flex-grow-0">
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
                style={{ paddingRight: "2.5em" }}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
            <button
              className="text-xl font-bold hover:text-primary sm:ml-2"
              onClick={onClose}
            >
              &times;
            </button>
          </div>
        </div>
        
        <div className="p-4 max-h-[60vh] overflow-auto">
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr>
                  <th className="border px-2 py-1 sm:px-4 sm:py-2 text-left">Month</th>
                  <th className="border px-2 py-1 sm:px-4 sm:py-2 text-left">Balance Amount</th>
                  <th className="border px-2 py-1 sm:px-4 sm:py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(historyData?.paymentsByMonth || {}).map(
                  ([month, { balanceAmount, status }], index) => {
                    // Check if this is a future month and property is unoccupied
                  //  const isPropertyVacant = historyData?.propertyOccupied !== "Occupied";
                   // const isFuture = isFutureMonth(month);
                  //  const displayNil = isPropertyVacant && isFuture;
                    // const displayNil = isFuture;
                    
                    // Display data based on conditions
                    const displayStatus =  status === "Unoccupied" ? "Nil" : status;
                    const displayBalance = status === "Unoccupied" ? "Nil" : balanceAmount;
                    
                    return (
                      <tr
                        key={index}
                        className={`${index % 2 === 0 ? "bg-gray-100" : "bg-white"}`}
                      >
                        <td className="border px-2 py-1 sm:px-4 sm:py-2">{month}</td>
                        <td className="border px-2 py-1 sm:px-4 sm:py-2">
                          {displayBalance}
                        </td>
                        <td className="border px-2 py-1 sm:px-4 sm:py-2">
                          <span>
                            {displayStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end p-4">
          <button
            className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-secondary transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupHistory;

